import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { apiService, queryKeys } from '@/services/api';
import type { DivisionInfo, DivisionPlayer } from '@/types/api';
import { PlayerHistoryChart } from './PlayerHistoryChart';
import HeadToHeadModal from './HeadToHeadModal';
import { ALL_GAME_TABS, getDivisionInfoById, inferAvailableGameTabs } from '@/utils/divisionGameTabs';
import {
  buildYearRangeOptions,
  getMaxAvailableYears,
} from '@/utils/historyRangeOptions';

function formatDivisionLabel(div: DivisionInfo, isCurrent: boolean): string {
  const fmt = (s: string) => {
    const d = new Date(s.includes('T') ? s : s + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };
  const dates = div.first_match_date && div.last_match_date
    ? ` (${fmt(div.first_match_date)}–${fmt(div.last_match_date)})`
    : '';
  const tag = isCurrent ? ' ★' : '';
  return `#${div.division_id}${dates}${tag}`;
}

interface PlayerHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  playerId: string;
  playerName: string;
  initialGameType?: '8-ball' | '9-ball' | '10-ball';
  divisionId?: string;
  /** Division players for opponent name → player ID resolution. */
  divisionPlayers?: DivisionPlayer[];
}

type GameTypeSelection = '8-ball' | '9-ball' | '10-ball' | 'all';

const GAME_TYPE_OPTIONS: { value: GameTypeSelection; label: string }[] = [
  { value: '8-ball', label: '8-Ball' },
  { value: '9-ball', label: '9-Ball' },
  { value: '10-ball', label: '10-Ball' },
  { value: 'all', label: 'All' },
];

const TIME_RANGE_OPTIONS: { value: number | undefined; label: string }[] = [
  { value: 30, label: '30d' },
  { value: 90, label: '90d' },
  { value: 180, label: '6mo' },
];

export const PlayerHistoryModal: React.FC<PlayerHistoryModalProps> = ({
  isOpen,
  onClose,
  playerId,
  playerName,
  initialGameType = '8-ball',
  divisionId,
  divisionPlayers,
}) => {
  const [selectedGameType, setSelectedGameType] = useState<GameTypeSelection>(initialGameType);
  const [selectedDays, setSelectedDays] = useState<number | undefined>(divisionId ? undefined : 90);
  const [selectedDivision, setSelectedDivision] = useState<string | undefined>(divisionId);
  const [groupMatchDays, setGroupMatchDays] = useState(true);
  const [h2hTarget, setH2hTarget] = useState<{ playerId: string; playerName: string; opponentId: string; opponentName: string } | null>(null);

  const { data: divisions } = useQuery({
    queryKey: queryKeys.divisions,
    queryFn: () => apiService.getDivisions(),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const timelineGameType = selectedGameType === 'all' ? '8-ball' : selectedGameType;

  const { data: fullRangeData } = useQuery({
    queryKey: queryKeys.csrTimeline(playerId, timelineGameType, undefined, undefined),
    queryFn: () => apiService.getCSRTimeline(playerId, timelineGameType, undefined, undefined),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen && !selectedDivision,
  });

  const selectedDivisionInfo = useMemo(
    () => getDivisionInfoById(divisions, selectedDivision),
    [divisions, selectedDivision]
  );

  const availableGameTypes = useMemo(() => {
    const tabs = inferAvailableGameTabs(selectedDivisionInfo?.name);
    const perGame = GAME_TYPE_OPTIONS.filter(
      (option) => option.value !== 'all' && tabs.includes(option.value.replace('-', '') as typeof ALL_GAME_TABS[number])
    );
    // Show "All" tab when more than one game type is available
    if (perGame.length > 1) {
      return [...perGame, { value: 'all' as GameTypeSelection, label: 'All' }];
    }
    return perGame;
  }, [selectedDivisionInfo?.name]);

  const yearRangeOptions = useMemo(
    () => buildYearRangeOptions(getMaxAvailableYears([fullRangeData?.timeline])),
    [fullRangeData?.timeline]
  );

  const timeRangeDropdownOptions = useMemo(() => {
    const fixed = TIME_RANGE_OPTIONS.map((opt) => ({
      value: String(opt.value),
      label: opt.label,
    }));
    const yearly = yearRangeOptions.map((opt) => ({
      value: opt.days == null ? 'all' : String(opt.days),
      label: opt.label,
    }));
    return [...fixed, ...yearly];
  }, [yearRangeOptions]);

  useEffect(() => {
    if (!availableGameTypes.some((option) => option.value === selectedGameType)) {
      setSelectedGameType(availableGameTypes[0]?.value ?? '8-ball');
    }
  }, [availableGameTypes, selectedGameType]);

  const handleHeadToHead = useCallback(
    (_triggeredPlayerId: string, opponentName: string) => {
      if (!divisionPlayers) return;
      // Collapse multiple spaces and normalize for comparison
      const norm = (s: string) => s.toLowerCase().trim().replace(/\s+/g, ' ');
      const normalizedOpponent = norm(opponentName);
      // Exact match (after whitespace normalization)
      let opponent = divisionPlayers.find(
        (p) => norm(p.name) === normalizedOpponent
      );
      // Contains fallback
      if (!opponent) {
        opponent = divisionPlayers.find((p) => {
          const n = norm(p.name);
          return n.includes(normalizedOpponent) || normalizedOpponent.includes(n);
        });
      }
      if (opponent) {
        setH2hTarget({
          playerId,
          playerName,
          opponentId: opponent.player_id,
          opponentName: opponent.name,
        });
      } else {
        console.warn(`[H2H] Could not resolve opponent "${opponentName}" in division players`);
      }
    },
    [divisionPlayers, playerId, playerName]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-4xl md:max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-900">{playerName}</h2>
            <p className="text-sm text-slate-400">Player #{playerId}</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 md:gap-4 px-4 md:px-6 py-3 border-b border-slate-100 bg-slate-50/50">
          {/* Game Type */}
          <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
            {availableGameTypes.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSelectedGameType(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  selectedGameType === opt.value
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Time Range — hidden when division is selected */}
          {!selectedDivision && (
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-slate-400">Range</span>
              <select
                value={selectedDays == null ? 'all' : String(selectedDays)}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedDays(val === 'all' ? undefined : Number(val));
                }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                {timeRangeDropdownOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Division/Season filter */}
          {divisions && divisions.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                value={selectedDivision || ''}
                onChange={(e) => {
                  const val = e.target.value;
                  setSelectedDivision(val || undefined);
                }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                <option value="">All Divisions</option>
                {divisions.map((div: DivisionInfo, idx: number) => (
                  <option key={div.division_id} value={div.division_id}>
                    {formatDivisionLabel(div, idx === 0)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <label className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
            <input
              type="checkbox"
              checked={groupMatchDays}
              onChange={(e) => setGroupMatchDays(e.target.checked)}
              className="rounded border-slate-300 text-brand-600 focus:ring-brand-300"
            />
            Group match days
          </label>
        </div>

        {/* Chart */}
        <div className="flex-1 overflow-auto p-6">
          <PlayerHistoryChart
            playerId={playerId}
            playerName={playerName}
            gameType={selectedGameType}
            days={selectedDays}
            divisionId={selectedDivision}
            groupMatchDays={groupMatchDays}
            onHeadToHead={divisionPlayers ? handleHeadToHead : undefined}
          />
        </div>
      </div>

      {/* Head to Head Modal */}
      {h2hTarget && (
        <HeadToHeadModal
          isOpen={true}
          onClose={() => setH2hTarget(null)}
          playerAId={h2hTarget.playerId}
          playerAName={h2hTarget.playerName}
          playerBId={h2hTarget.opponentId}
          playerBName={h2hTarget.opponentName}
          initialGameType={selectedGameType === 'all' ? '8-ball' : selectedGameType}
          divisionId={divisionId}
        />
      )}
    </div>
  );
};

export default PlayerHistoryModal;
